import React, { useState, useMemo } from "react";
import { FiFilter } from "react-icons/fi";
import { COLORS, FUNNEL_STAGE_META, FUNNEL_WIDTH_STEPS } from "../myDashboardConstants";
import { SectionCard2 } from "../components/SectionCards";
import { RangeTabs } from "../components/RangeTabs";

/* ===== from sections/SalesFunnel.jsx ===== */
export function SalesFunnel({ data1, range, onRangeChange }) {
  const [hoveredStage, setHoveredStage] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const data = useMemo(() => {
    const src = data1 || {};
    // Fixed order: Leads -> Deals -> Quotation -> Sales Order -> Invoice
    return Object.entries(FUNNEL_STAGE_META).map(([key, meta]) => ({
      id: key,
      name: meta.label,
      value: Number(src[key]) || 0,
      lastValue: Number(src[`last_${key}`]) || 0,
      fill: meta.fill,
    }));
  }, [data1]);

  const hasData = data.some((d) => d.value > 0);

  // Build stage rows: pct is current-vs-last (period over period), same rule
  // used for Outreach Activity / Activity Summary:
  //  - last = 0, current > 0  -> 100% (up)
  //  - last = 0, current = 0  -> 0%
  //  - last > 0, current = 0  -> -100% (down)
  //  - otherwise -> (current - last) / last * 100
  const funnelStages = data.map((d, i) => {
    const currentVal = d.value;
    const lastVal = d.lastValue;
    const diff = currentVal - lastVal;
    const isUp = diff >= 0;
    const pctNum =
      lastVal === 0
        ? (currentVal > 0 ? 100 : 0)
        : Math.abs((diff / lastVal) * 100);
    const pctLabel = `${isUp ? "▲" : "▼"} ${pctNum.toFixed(1)}%`;

    return {
      id: d.id,
      stage: d.name,
      count: currentVal.toLocaleString(),
      pct: pctLabel,
      pctColor: diff === 0 ? COLORS.warning : isUp ? COLORS.success : COLORS.danger,
      width: FUNNEL_WIDTH_STEPS[i] || "45%",
      bgGradient: hasData
        ? `linear-gradient(135deg, ${d.fill}17 0%, ${d.fill}2E 100%)`
        : "linear-gradient(135deg, #f1f4f9 0%, #e7ebf2 100%)",
      borderColor: hasData ? `${d.fill}55` : "#dfe4ec",
      textColor: hasData ? d.fill : COLORS.muted,
      insight: `${currentVal.toLocaleString()} this period vs ${lastVal.toLocaleString()} last period.`,
    };
  });

  const handleMouseEnter = (item, e) => {
    setHoveredStage(item);
    const rect = e.currentTarget.getBoundingClientRect();
    const parentRect = e.currentTarget.parentElement.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - parentRect.top - 8,
      left: rect.left - parentRect.left + rect.width / 2,
    });
  };

  const handleMouseLeave = () => setHoveredStage(null);

  return (
    <SectionCard2
      title="Sales Funnel"
      subtitle="Lead to won conversion"
      icon={<FiFilter size={16} />}
      accent={COLORS.info}
      action={<RangeTabs value={range} onChange={onRangeChange} />}
    >
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 8,
          width: "100%",
          paddingTop: 4,
        }}
      >
        {funnelStages.map((item, idx) => {
          const isHovered = hoveredStage?.id === item.id;
          const isAnyHovered = hoveredStage !== null;

          return (
            <div
              key={item.id}
              onMouseEnter={(e) => handleMouseEnter(item, e)}
              onMouseLeave={handleMouseLeave}
              style={{
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "0 24px",
                borderRadius: 10,
                width: item.width,
                height: "60px",
                background: item.bgGradient,
                border: `1.5px solid ${item.borderColor}`,
                boxShadow: isHovered
                  ? "0 10px 20px rgba(0,0,0,0.08), 0 2px 6px rgba(0,0,0,0.04)"
                  : "0 2px 4px rgba(0,0,0,0.02)",
                clipPath:
                  idx < funnelStages.length - 1
                    ? "polygon(0 0, 100% 0, 94% 100%, 7% 100%)"
                    : "polygon(0 0, 100% 0, 92% 100%, 7% 100%)",
                transform: isHovered ? "scale(1.03) translateY(-2px)" : "scale(1)",
                opacity: isHovered ? 1 : isAnyHovered ? 0.4 : 1,
                filter: isAnyHovered && !isHovered ? "grayscale(20%)" : "none",
                transition: "transform 0.2s ease, opacity 0.2s ease, filter 0.2s ease",
                cursor: "pointer",
                zIndex: isHovered ? 10 : 1,
              }}
            >
              <span
                style={{
                  fontWeight: 700,
                  paddingLeft: 8,
                  color: item.textColor,
                  fontSize: "12.5px",
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                }}
              >
                {item.stage}
              </span>

              <div style={{ paddingRight: 8, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={{ fontWeight: 700, color: item.textColor, fontSize: "13px" }}>
                  {item.count}
                </span>
                <span
                  style={{
                    display: "inline-block",
                    padding: "3px 9px",
                    borderRadius: 999,
                    fontWeight: 700,
                    fontSize: "10px",
                    backgroundColor: `${item.pctColor}1A`,
                    color: item.pctColor,
                    border: `1px solid ${item.pctColor}44`,
                  }}
                >
                  {item.pct}
                </span>
              </div>
            </div>
          );
        })}

        {hoveredStage && (
          <div
            style={{
              position: "absolute",
              top: `${tooltipPos.top}px`,
              left: `${tooltipPos.left}px`,
              transform: "translate(-50%, -100%)",
              borderRadius: 10,
              padding: 12,
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(8px)",
              border: `1px solid ${hoveredStage.borderColor}`,
              boxShadow: "0 10px 30px rgba(15,23,42,0.15)",
              zIndex: 100,
              minWidth: "200px",
              maxWidth: "260px",
              pointerEvents: "none",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontWeight: 700, color: hoveredStage.textColor, fontSize: "12px" }}>
                {hoveredStage.stage}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "2px 8px",
                  borderRadius: 999,
                  fontSize: "9px",
                  fontWeight: 700,
                  background: hoveredStage.pctColor,
                  color: "#fff",
                }}
              >
                {hoveredStage.pct}
              </span>
            </div>
            <p style={{ margin: 0, color: "#64748B", fontSize: "10.5px", lineHeight: 1.4 }}>
              {hoveredStage.insight}
            </p>
          </div>
        )}
      </div>
    </SectionCard2>
  );
}
